import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, Plus, ArrowRight, Archive, ArrowCounterClockwise, Trash, X, PencilSimple, Clock } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { fmtDateFR } from '../utils/dates';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  programme: { bg: '#E3F2FD', color: '#1565C0', label: 'Programme' },
  en_cours: { bg: '#E8F5E9', color: '#2E7D32', label: 'En cours' },
  termine: { bg: '#F4F5F7', color: '#5E5F66', label: 'Termine' },
};

const fmtDate = (iso) => { if (!iso) return '-'; try { return fmtDateFR(iso); } catch { return '-'; } };

const ProgrammingPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showArchives, setShowArchives] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', client_name: '', address: '', amount_ht: 0, start_date: '', end_date: '', notes: '', status: 'programme' });

  const fetchItems = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/scheduling`, { params: { only_archived: showArchives }, withCredentials: true });
      setItems(res.data);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  }, [showArchives]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const resetForm = () => { setEditing(null); setForm({ name: '', client_name: '', address: '', amount_ht: 0, start_date: '', end_date: '', notes: '', status: 'programme' }); };
  const openEdit = (it) => { setEditing(it); setForm({ name: it.name, client_name: it.client_name, address: it.address, amount_ht: it.amount_ht, start_date: it.start_date, end_date: it.end_date, notes: it.notes, status: it.status }); setShowForm(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await axios.put(`${API_URL}/api/scheduling/${editing.id}`, form, { withCredentials: true });
      else await axios.post(`${API_URL}/api/scheduling`, form, { withCredentials: true });
      toast.success(editing ? 'Mis a jour' : 'Cree');
      setShowForm(false); resetForm(); fetchItems();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleConvert = async (id, name) => {
    if (!window.confirm(`Convertir "${name}" en chantier actif ?`)) return;
    try {
      await axios.post(`${API_URL}/api/scheduling/${id}/convert-to-project`, {}, { withCredentials: true });
      toast.success('Chantier cree et en cours');
      fetchItems();
    } catch { toast.error('Erreur'); }
  };

  const handleArchive = async (id) => {
    try { await axios.put(`${API_URL}/api/scheduling/${id}/archive`, {}, { withCredentials: true }); toast.success('Archive'); fetchItems(); }
    catch { toast.error('Erreur'); }
  };
  const handleUnarchive = async (id) => {
    try { await axios.put(`${API_URL}/api/scheduling/${id}/unarchive`, {}, { withCredentials: true }); toast.success('Restaure'); fetchItems(); }
    catch { toast.error('Erreur'); }
  };
  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer definitivement ?')) return;
    try { await axios.delete(`${API_URL}/api/scheduling/${id}`, { withCredentials: true }); toast.success('Supprime'); fetchItems(); }
    catch { toast.error('Erreur'); }
  };

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;

  return (
    <div data-testid="programming-page">
      <div className="page-header">
        <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><Calendar size={26} weight="bold" /> Programmation</h1>
        <p className="page-subtitle">Chantiers programmes issus des devis valides</p>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', gap: '0', borderBottom: '2px solid #E2E2E6' }}>
          <button onClick={() => setShowArchives(false)} style={{ padding: '8px 14px', border: 'none', cursor: 'pointer', fontWeight: '600', background: 'transparent', borderBottom: !showArchives ? '2px solid #002FA7' : '2px solid transparent', color: !showArchives ? '#002FA7' : '#5E5F66', marginBottom: '-2px' }} data-testid="tab-active">Actives ({!showArchives ? items.length : '-'})</button>
          <button onClick={() => setShowArchives(true)} style={{ padding: '8px 14px', border: 'none', cursor: 'pointer', fontWeight: '600', background: 'transparent', borderBottom: showArchives ? '2px solid #002FA7' : '2px solid transparent', color: showArchives ? '#002FA7' : '#5E5F66', marginBottom: '-2px' }} data-testid="tab-archives">Archives</button>
        </div>
        {isAdmin && !showArchives && <button className="btn btn-primary" onClick={() => { resetForm(); setShowForm(true); }} data-testid="add-scheduling-btn"><Plus size={16} /> Nouvelle programmation</button>}
      </div>

      {items.length === 0 ? (
        <div className="card" style={{ padding: '48px', textAlign: 'center', color: '#5E5F66' }}><Calendar size={48} style={{ opacity: 0.4, marginBottom: '12px' }} /><p>{showArchives ? 'Aucun archive' : 'Aucun chantier programme'}</p></div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '16px' }}>
          {items.map(it => {
            const status = STATUS_COLORS[it.status] || STATUS_COLORS.programme;
            const d = it.days_until_start;
            let countdown = null;
            if (d !== null && d !== undefined && !showArchives) {
              if (d < 0) countdown = { color: '#C62828', bg: '#FFEBEE', label: `En retard ${Math.abs(d)}j` };
              else if (d === 0) countdown = { color: '#E65100', bg: '#FFF3E0', label: 'AUJOURD\'HUI' };
              else if (d <= 7) countdown = { color: '#E65100', bg: '#FFF3E0', label: `J-${d} jour${d > 1 ? 's' : ''}` };
              else countdown = { color: '#1565C0', bg: '#E3F2FD', label: `J-${d}` };
            }
            return (
              <div key={it.id} className="card" style={{ overflow: 'hidden' }}>
                <div style={{ padding: '14px 18px', borderBottom: '1px solid #F4F5F7', display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: '8px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: '700', fontSize: '1rem' }}>{it.name}</div>
                    <div style={{ fontSize: '0.85rem', color: '#5E5F66' }}>{it.client_name || '-'}</div>
                  </div>
                  <span style={{ padding: '3px 10px', borderRadius: '4px', fontSize: '0.7rem', fontWeight: '700', backgroundColor: status.bg, color: status.color }}>{status.label}</span>
                </div>
                {countdown && (
                  <div style={{ padding: '10px 18px', backgroundColor: countdown.bg, color: countdown.color, fontSize: '0.85rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Clock size={14} weight="bold" /> {countdown.label}
                  </div>
                )}
                <div style={{ padding: '12px 18px', fontSize: '0.85rem', display: 'grid', gap: '6px' }}>
                  <div>Debut : <strong>{fmtDate(it.start_date)}</strong></div>
                  <div>Fin : <strong>{fmtDate(it.end_date)}</strong></div>
                  <div>Montant HT : <strong>{(it.amount_ht || 0).toLocaleString('fr-FR')} EUR</strong></div>
                  {it.notes && <div style={{ color: '#5E5F66', fontStyle: 'italic' }}>{it.notes}</div>}
                </div>
                {isAdmin && (
                  <div style={{ padding: '10px 18px', borderTop: '1px solid #F4F5F7', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {showArchives ? (
                      <>
                        <button className="btn btn-primary btn-sm" onClick={() => handleUnarchive(it.id)}><ArrowCounterClockwise size={12} /> Restaurer</button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(it.id)}><Trash size={12} /></button>
                      </>
                    ) : (
                      <>
                        {!it.project_id && <button className="btn btn-primary btn-sm" onClick={() => handleConvert(it.id, it.name)} data-testid={`convert-${it.id}`}><ArrowRight size={12} /> Convertir en chantier</button>}
                        <button className="btn btn-secondary btn-sm" onClick={() => openEdit(it)}><PencilSimple size={12} /></button>
                        <button className="btn btn-secondary btn-sm" onClick={() => handleArchive(it.id)} title="Archiver"><Archive size={12} /></button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(it.id)}><Trash size={12} /></button>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '6px', width: '100%', maxWidth: '560px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0 }}>{editing ? 'Modifier la programmation' : 'Nouvelle programmation'}</h3>
              <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} style={{ padding: '20px' }}>
              <div className="form-group"><label className="form-label">Nom *</label><input type="text" className="form-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
              <div className="form-group"><label className="form-label">Client</label><input type="text" className="form-input" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} /></div>
              <div className="form-group"><label className="form-label">Adresse</label><input type="text" className="form-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="form-group"><label className="form-label">Date de debut *</label><input type="date" className="form-input" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} required /></div>
                <div className="form-group"><label className="form-label">Date de fin *</label><input type="date" className="form-input" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} required /></div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="form-group"><label className="form-label">Montant HT</label><input type="number" step="0.01" className="form-input" value={form.amount_ht} onChange={(e) => setForm({ ...form, amount_ht: parseFloat(e.target.value) || 0 })} /></div>
                <div className="form-group"><label className="form-label">Statut</label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent className="z-[9999]">
                      <SelectItem value="programme">Programme</SelectItem>
                      <SelectItem value="en_cours">En cours</SelectItem>
                      <SelectItem value="termine">Termine</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="form-group"><label className="form-label">Notes</label><textarea className="form-input" rows="3" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
                <button type="button" className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setShowForm(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>{editing ? 'Enregistrer' : 'Creer'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgrammingPage;
